import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusCircle, Eye } from "react-feather";
import MasterDetailListShell from "../../../components/layout/MasterDetailListShell";
import MasterDetailRow from "../../../components/layout/MasterDetailRow";
import StatusBadge from "../../../components/common/StatusBadge";
import LogWorkModal from "./LogWorkModal";
import {
  TECHNICAL_STATUS_CLASS,
  TECHNICAL_STATUS_LABEL,
  TECHNICAL_STATUS_FILTERS,
  TECHNICAL_SOURCE_FILTERS,
  TECHNICAL_SOURCE_LABEL,
} from "../technicalStatusMaps";
import {
  formatElapsedSince,
  formatTurnaroundDays,
  isFinalized,
  finishedDateOf,
  elapsedFromOf,
  sortWorkQueue,
} from "../workElapsed";
import { useGetTechnicalWorkQuery } from "../technicalApi";
import { useCanLogComplaint, useCanCreateServiceVisit } from "../useTechnicalPermissions";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { paths } from "../../../routes/paths";

// The pill Flutter's own work list carries, same numbers: hours while a job is
// live ("2.11hrs", red past 24), calendar-day turnaround once it has finished.
// A finished job's clock stops - it shows how long the job TOOK, in neutral
// grey, never the red "you are late" colour it has already escaped.
const AgePill = ({ row }) => {
  if (isFinalized(row)) {
    const turnaround = formatTurnaroundDays(row.createdDate, finishedDateOf(row));
    if (!turnaround) return null;
    return (
      <span className="badge me-2" style={{ background: "#EAEDF0", color: "#4A5560", fontWeight: 700, fontSize: 10.5 }}>
        {turnaround}
      </span>
    );
  }

  const elapsed = formatElapsedSince(elapsedFromOf(row));
  if (!elapsed) return null;
  return (
    <span
      className="badge me-2"
      style={{
        background: elapsed.isLong ? "#FDECEA" : "#E8F5EE",
        color: elapsed.isLong ? "#C62828" : "#2E7D32",
        fontWeight: 700,
        fontSize: 10.5,
      }}
      title={`Logged ${new Date(row.createdDate).toLocaleString()}`}
    >
      {elapsed.label}
    </span>
  );
};

const WorkCompactList = () => {
  const navigate = useNavigate();
  const { recordGuid: selectedRecordGuid } = useParams();
  const canLogComplaint = useCanLogComplaint();
  const canCreateServiceVisit = useCanCreateServiceVisit();
  const { selectedBranchId } = useBranchFilter();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [view, setView] = useState("");
  const [logType, setLogType] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Type and status are real server-side filters (`source` and `view`); the
  // free-text box stays client-side over the fetched page, because the
  // server's own customerName filter is a prefix match only and would miss
  // "alarms" inside "Texas Alarms Ltd" - the same reason Vouchers filters
  // its text search locally.
  const { data, isLoading } = useGetTechnicalWorkQuery({
    branchId: selectedBranchId ?? undefined,
    source: source || undefined,
    view: view || undefined,
    pageSize: 200,
  });

  // Re-ordered here rather than server-side: the proc returns newest-first,
  // which put every freshly closed job at the top of a queue of work nobody had
  // touched yet. This is a worklist, so it reads Not Started, then Assigned,
  // then what is in hand, with the finished rows last - see sortWorkQueue.
  const items = sortWorkQueue(
    (data?.items || []).filter((w) => {
      if (!search) return true;
      const needle = search.toLowerCase();
      return (
        w.workNumber?.toLowerCase().includes(needle) ||
        w.customerName?.toLowerCase().includes(needle) ||
        w.customerPhone?.toLowerCase().includes(needle) ||
        w.orderNumber?.toLowerCase().includes(needle) ||
        w.complaintDescription?.toLowerCase().includes(needle)
      );
    })
  );

  const canCreate = canLogComplaint || canCreateServiceVisit;

  return (
    <>
      <MasterDetailListShell
        title="Technical Work"
        addButton={
          canCreate && (
            <div className="dropdown">
              <button
                type="button"
                className="btn btn-added dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <PlusCircle className="me-2" size={16} />
                Log Work
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                {canLogComplaint && (
                  <li>
                    <button type="button" className="dropdown-item" onClick={() => setLogType("COMPLAINT")}>
                      Customer Complaint
                    </button>
                  </li>
                )}
                {canCreateServiceVisit && (
                  <li>
                    <button type="button" className="dropdown-item" onClick={() => setLogType("SERVICE_VISIT")}>
                      Service Visit
                    </button>
                  </li>
                )}
              </ul>
            </div>
          )
        }
        filters={
          <>
            <input
              type="text"
              placeholder="Search work no, customer, phone or description"
              className="form-control form-control-sm"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <div className="d-flex gap-2 mt-2">
              <select className="form-control form-control-sm" value={source} onChange={(e) => setSource(e.target.value)}>
                {TECHNICAL_SOURCE_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select className="form-control form-control-sm" value={view} onChange={(e) => setView(e.target.value)}>
                {TECHNICAL_STATUS_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </>
        }
        isLoading={isLoading}
        emptyLabel="No technical work matches these filters."
        items={items}
        getKey={(w) => w.recordGuid}
        resetKey={`${search}|${source}|${view}|${selectedBranchId}`}
        renderItem={(w) => {
          const isSelected = String(w.recordGuid) === String(selectedRecordGuid);
          return (
            <MasterDetailRow
              isSelected={isSelected}
              onClick={() => navigate(paths.technicalWorkDetail(w.recordGuid))}
              avatarLabel={(w.customerName || "?").trim().slice(0, 2).toUpperCase()}
              title={
                // The customer is what people scan for; the TW-/SV- number is
                // reference detail (2026-09-12, same rule on every list).
                <>{w.customerName || w.workNumber}</>
              }
              subtitle={
                <>
                  {w.workNumber} &middot; {TECHNICAL_SOURCE_LABEL[w.sourceType] || w.sourceType} &middot; {w.branchCode}
                </>
              }
              badge={
                <span className="me-2 d-inline-flex align-items-center">
                  <AgePill row={w} />
                  <StatusBadge status={w.status} classMap={TECHNICAL_STATUS_CLASS} labelMap={TECHNICAL_STATUS_LABEL} />
                </span>
              }
              actions={
                <button
                  type="button"
                  className="btn btn-link p-1"
                  title="View"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(paths.technicalWorkDetail(w.recordGuid));
                  }}
                >
                  <Eye size={15} />
                </button>
              }
            />
          );
        }}
      />
      {/* Modal, not a nested "new" route: both entry forms need a full-width
          customer search and a technician multi-select, neither of which fits
          the split view's narrow detail pane. Same reasoning as Quotations'
          own create modal. */}
      <LogWorkModal show={Boolean(logType)} sourceType={logType} onClose={() => setLogType(null)} />
    </>
  );
};

export default WorkCompactList;
